import { useQuery } from "@apollo/client"
import React, { useCallback, useEffect, useMemo, useState } from "react"

import { ATBConfirmation } from "basket_and_checkout/shared/components/ATBConfirmation"
import { QuizInsert } from "components/Layouts/Header/Search/QuizInsert"
import { ChevronRightIcon, CloseIcon } from "components/SVG"
import { SEARCH_SUGGESTION_QUERY } from "data/plp/gql"
import { useChatVisibility } from "hooks"
import { useDebounce } from "hooks/useDebounce"
import { getClient } from "lib/apolloClient"
import { DeprecatedUISharedButton } from "packages/ui-shared/components/Button/Button"
import { OverlayDrawer } from "packages/ui-shared/components/OverlayDrawer/OverlayDrawer"
import { useSegment } from "tracking"
import type {
  MultipleProductCard,
  SearchProducts,
  SingularProductCard,
  VariantType,
} from "types"
import { trackSearch } from "utils/gtm"
import { navigate } from "utils/routes"

import { SearchBar } from "./SearchBar/SearchBar"
import * as styles from "./SearchV2.css"
import {
  isSearchError,
  isSearchSuggestionsResult,
  sanitizeQueryStringParam,
} from "./utils"
import { useParams } from "next/navigation"
import { getTypicalJourneyStatus } from "packages/acquisition/GuestPrice/lib/typicalJourney"
import cookies from "js-cookie"
import { ProductCardSearch } from "packages/core-shopping/components/ProductCardSearch/ProductCardSearch"
import { useUser } from "lib/user"
import { DefaultComponentOnSearch } from "components/Layouts/Header/Search/DefaultComponentOnSearch"
import { colours } from "@shared/ui.design-system"

type SearchResults = {
  products: (MultipleProductCard | SingularProductCard)[]
}

const emptySearchResults = {
  products: [],
}

const DEBOUNCE_INTERVAL = 400
const MIN_SEARCH_LENGTH = 2

export const SearchV2: React.FC<{
  isSearchOpen: boolean
  closeSearch: () => void
}> = ({ isSearchOpen, closeSearch }) => {
  const [searchValue, setSearchValue] = useState("")
  const [searchResults, setSearchResults] =
    useState<SearchResults>(emptySearchResults)

  const { track } = useSegment()

  const { isLoggedIn } = useUser()

  const debouncedSearchValue = useDebounce(searchValue, DEBOUNCE_INTERVAL)

  // N.B: We need to update src/packages/core-shopping/shared/components/Search/Search.test.tsx mocks once useGuestPrice changes 👇
  // Relevant code can be found in src/packages/core-shopping/shared/components/Search/Search.test.tsx with this tag: [remove-use-guest-price]
  useChatVisibility(isSearchOpen)

  const sanitizeSearchValue = useCallback(
    () => sanitizeQueryStringParam(searchValue),
    [searchValue]
  )

  const urlParams = useParams()
  const allProductsHref = useMemo(() => {
    const href = `shop/search?term=${sanitizeSearchValue()}`

    if (urlParams.store === "us") {
      return `/us/${href}`
    }

    return `/${href}`
  }, [urlParams, sanitizeSearchValue])

  const [confirmation, setConfirmation] = useState<boolean>(false)
  const [confirmationVariant, setConfirmationVariant] =
    useState<VariantType | null>(null)

  const isATBVisible = confirmation && confirmationVariant !== null
  const { data, error, loading } = useQuery<{ searchProducts: SearchProducts }>(
    SEARCH_SUGGESTION_QUERY,
    {
      variables: {
        term: debouncedSearchValue,
        isTypicalJourney: getTypicalJourneyStatus(cookies.get()),
      },
      client: getClient(),
      skip: debouncedSearchValue.length < MIN_SEARCH_LENGTH,
    }
  )

  useEffect(() => {
    // todo: implement a loading spinner
    if (loading) return
    // todo: implement an error message
    if (error) return setSearchResults(emptySearchResults)

    const searchSuggestions = data?.searchProducts?.searchSuggestions

    if (isSearchSuggestionsResult(searchSuggestions)) {
      return setSearchResults({
        products: searchSuggestions?.products ?? [],
      })
    }

    if (isSearchError(searchSuggestions)) {
      // todo: implement an error message
      return setSearchResults(emptySearchResults)
    }
  }, [data, error, loading])

  const setSearchAndSuggest = (value) => {
    setSearchValue(value)
    if (value.length < MIN_SEARCH_LENGTH) {
      setSearchResults(emptySearchResults)
    }
  }

  const onSubmit = () => {
    const sanitizedSearchValue = sanitizeSearchValue()

    track(
      {
        event: "Products Searched",
        properties: {
          query: sanitizedSearchValue,
        },
      },
      {
        uiEvent: {
          target: "search button",
        },
      }
    )
    trackSearch({
      resultContent: `/shop/search?term=${sanitizedSearchValue}`,
      resultReturned: !!searchResults.products.length, // add suggested categories
      label: "suggested keyword",
      value: sanitizedSearchValue,
    })
    closeSearch()
    navigate(`/shop/search?term=${sanitizedSearchValue}`)
  }

  const onClose = () => {
    // reset input value and close search panel
    track({
      event: "Page Element Clicked",
      properties: {
        module_content: "Close Search",
        module_content_details: searchValue,
        module_content_details2: `${searchResults.products.length}`,
      },
    })
    setSearchValue("")
    setSearchResults(emptySearchResults)
    closeSearch()
  }

  const handleShowAllProducts = () => {
    const sanitizedSearchValue = sanitizeSearchValue()
    track(
      {
        event: "Products Searched",
        properties: {
          query: sanitizedSearchValue,
        },
      },
      {
        uiEvent: {
          target: "show all products button",
        },
      }
    )
    trackSearch({
      resultContent: `/shop/search?term=${sanitizedSearchValue}`,
      resultReturned: true,
      label: "suggested keyword",
      value: sanitizedSearchValue,
    })
    onClose()
  }
  const onFocus = async () => {
    track({
      event: "Search Input Focus",
      properties: {},
    })
  }

  const componentToShow = isLoggedIn ? (
    <DefaultComponentOnSearch />
  ) : (
    <QuizInsert />
  )

  return (
    <>
      {isATBVisible && (
        <ATBConfirmation
          confirmation={confirmation}
          setConfirmation={setConfirmation}
          variant={confirmationVariant}
          source="Search"
        />
      )}
      <OverlayDrawer isOpen={isSearchOpen} handleDismiss={onClose} size="fill">
        <div
          className={styles.header}
          data-testid="search-panel-v2"
          id="search-panel"
        >
          <SearchBar
            value={searchValue}
            onChange={setSearchAndSuggest}
            onSubmit={onSubmit}
            onFocus={onFocus}
            onBack={onClose}
            placeholder="By: name, ingredient, usage..."
            isSearchOpen={isSearchOpen}
          />
          <CloseIcon
            data-testid="close-search"
            aria-label="Close Icon"
            className={styles.closeIcon}
            onClick={onClose}
          />
        </div>

        <div className={styles.body}>
          {!searchResults.products.length ? (
            componentToShow
          ) : (
            <>
              <h3 className={styles.title}>Matching Products</h3>
              <div className={styles.productCardLayout}>
                {searchResults.products.map((productCard, index, allItems) => (
                  <div
                    key={`search-product-card-${productCard.id}`}
                    className={styles.productCardWrapper}
                    data-event-intersection="productImpression"
                    data-sku={productCard.variants[0].sku}
                    data-testid="search-product-card"
                  >
                    <ProductCardSearch
                      searchTerm={debouncedSearchValue}
                      productCard={productCard}
                      closeSearch={onClose}
                      setConfirmation={setConfirmation}
                      setConfirmationVariant={setConfirmationVariant}
                      list={allItems}
                      listPosition={index}
                    />
                  </div>
                ))}
              </div>
              <a
                className={styles.showAllButton}
                data-testid="ShowAllProducts"
                href={allProductsHref}
                onClick={() => {
                  handleShowAllProducts()
                }}
              >
                <DeprecatedUISharedButton
                  ariaLabel="Show all products"
                  variant="primary"
                  type="submit"
                >
                  Show all products
                  <ChevronRightIcon fill={colours.secondary.light} />
                </DeprecatedUISharedButton>
              </a>
            </>
          )}
        </div>
      </OverlayDrawer>
    </>
  )
}
